<template>
  <div
    class="ui-plugin ui-plugin-wrapper"
    :data-plugin-key="props.pluginKey ?? undefined"
    :data-plugin-optional="String(isOptionalResolved)"
    :data-plugin-natural-ready="String(naturalReady)"
    :data-plugin-forced="String(forced)"
    :data-plugin-effective-ready="String(effectiveReady)"
    :data-plugin-show-tasks="String(showTasks)"
    :data-plugin-index="props.pluginIndex != null ? String(props.pluginIndex) : undefined"
  >
    <!-- Debug block pleasde keep for now -->
    <!-- <pre class="mt-2 text-xs opacity-70">
    {{
        {
          forced,
          ready,
          naturalReady,
          effectiveReady,
          showTasks,
          isOptional: isOptionalResolved,
        }
      }}
    </pre> -->

    <SectionWrapper v-if="isPreview">
      <slot
        name="inline"
        :ready="ready"
        :showTasks="showTasks"
        :allReady="effectiveReady"
        :forcedReady="forced"
        :toggle="toggle"
        :forceContinue="forceContinue"
        :revertContinue="revertContinue"
      />
      <Divider class="mt-8" />

      <div v-if="showActions" class="mt-4 p-2" data-testid="ticket-plugin-actions">
        <slot
          name="actions"
          :ready="ready"
          :allReady="effectiveReady"
          :forcedReady="forced"
          :toggle="toggle"
          :forceContinue="forceContinue"
          :revertContinue="revertContinue"
        >
          <!-- Mandatory plugin -->
          <div v-if="!isOptionalResolved" class="flex items-center justify-between w-full">
            <span class="inline-flex items-center gap-2">
              <i
                :class="naturalReady ? iconOk : iconNotOk"
                :style="{ fontSize: statusSize, color: naturalReady ? colorOk : colorNotOk }"
              />
              <strong
                data-testid="ticket-plugin-status"
                :data-plugin-natural-ready="String(naturalReady)"
                :data-plugin-effective-ready="String(effectiveReady)"
              >
                {{ naturalReady ? tOk : tNotOk }}
              </strong>
            </span>

            <div v-if="naturalReady">
              <Button
                data-testid="ticket-plugin-toggle-tasks"
                :data-plugin-show-tasks="String(showTasks)"
                :label="showTasks ? tHide : tShow"
                :icon="showTasks ? iconHide || undefined : iconShow || undefined"
                outlined
                @click="() => (showTasks = !showTasks)"
              />
            </div>
            <div v-else>
              <div v-if="forced">
                <Button
                  data-testid="ticket-plugin-undo-force"
                  :icon="iconUndoForce"
                  :label="tUndoContinue"
                  @click="revertContinue"
                />
              </div>
              <div v-else>
                <Button
                  data-testid="ticket-plugin-force"
                  :icon="iconForce"
                  :label="tContinueAnyway"
                  outlined
                  @click="forceContinue"
                />
              </div>
            </div>
          </div>

          <!-- Optional plugin -->
          <div v-else class="flex items-center justify-between w-full">
            <span class="inline-flex items-center gap-2">
              <i
                v-if="ready"
                :class="effectiveReady ? iconOk : iconNotOk"
                :style="{ fontSize: statusSize, color: effectiveReady ? colorOk : colorNotOk }"
              />
              <strong>{{ ready ? tOk : t('text.optionalPlugin') }}</strong>
            </span>
            <Button
              :label="showTasks ? tHide : tShow"
              :icon="showTasks ? iconHide || undefined : iconShow || undefined"
              outlined
              @click="() => (showTasks = !showTasks)"
            />
          </div>
        </slot>
      </div>
    </SectionWrapper>

    <div v-else>
      <slot
        name="full"
        :ready="ready"
        :allReady="effectiveReady"
        :forcedReady="forced"
        :toggle="toggle"
        :forceContinue="forceContinue"
        :revertContinue="revertContinue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, nextTick, onMounted, type Ref, ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type BoolLike = boolean | Ref<boolean> | ComputedRef<boolean>;

type Emits = {
  (e: 'update:ready', v: boolean): void;
  (e: 'update:forced', v: boolean): void;
  (e: 'toggle', v: boolean): void;
  (e: 'lock'): void;
  (e: 'unlock'): void;
  (e: 'force-continue'): void;
  (e: 'revert-continue'): void;
};

const emit = defineEmits<Emits>();

const props = withDefaults(
  defineProps<{
    isPreview?: boolean;
    isOptional?: BoolLike;
    isPublic?: BoolLike;
    /** natural readiness reported by the plugin */
    naturalReady?: BoolLike;
    /** v-models */
    ready: BoolLike;
    forced?: BoolLike;
    /** UI */
    showActions?: boolean;

    /* Labels & icons */
    labelOk?: string;
    labelNotOk?: string;
    labelContinueAnyway?: string;
    labelUndoContinue?: string;
    labelLock?: string;
    labelUnlock?: string;

    okIcon?: string;
    notOkIcon?: string;
    okIconColor?: string;
    notOkIconColor?: string;
    statusIconSize?: string;

    forceIcon?: string;
    undoForceIcon?: string;

    showIcon?: string;
    hideIcon?: string;
    // testIdBase?: string;    // e.g. "ticket-plugin-BlueprintComponent"
    pluginKey?: string; // optional extra (nice for data attr)
    pluginIndex?: number; // optional index of the plugin in the list
  }>(),
  {
    isPreview: true,
    isOptional: false,
    isPublic: false,
    naturalReady: false,
    canForce: true,
    showActions: true,
    forced: false,

    labelOk: undefined,
    labelNotOk: undefined,
    labelContinueAnyway: undefined,
    labelUndoContinue: undefined,
    labelLock: undefined,
    labelUnlock: undefined,

    okIcon: undefined,
    notOkIcon: undefined,
    okIconColor: undefined,
    notOkIconColor: undefined,
    statusIconSize: undefined,

    forceIcon: undefined,
    undoForceIcon: undefined,

    showIcon: undefined,
    hideIcon: undefined,
    pluginKey: undefined,
    pluginIndex: undefined,
  },
);

const { t } = useI18n();

/** Hydration guard: prevent emits during initial mount */
const isHydrating = ref(true);
onMounted(async () => {
  await nextTick();
  isHydrating.value = false;
});

/** Unwrap BoolLikes */
const isOptionalResolved = computed(() => !!unref(props.isOptional));
const naturalReady = computed<boolean>(() => !!unref(props.naturalReady));
const forced = computed<boolean>({
  get: () => !!unref(props.forced),
  set: (v) => emit('update:forced', !!v),
});
const ready = computed<boolean>({
  get: () => !!unref(props.ready),
  set: (v) => {
    emit('update:ready', !!v);
    emit('toggle', !!v);
    if (v) {
      emit('unlock');
    } else {
      emit('lock');
    }
  },
});

/** Derived readiness */
const effectiveReady = computed<boolean>(() => forced.value || naturalReady.value);

/** Labels & icons */
const tOk = computed(() => props.labelOk ?? t('messages.uiPluginAllChecked'));
const tNotOk = computed(() => props.labelNotOk ?? t('messages.uiPluginNotAllChecked'));
const tContinueAnyway = computed(
  () => props.labelContinueAnyway ?? t('buttons.uiPluginContinueAnyway'),
);
const tUndoContinue = computed(() => props.labelUndoContinue ?? t('buttons.uiPluginUndoContinue'));
const tShow = computed(() => props.labelLock ?? t('buttons.show'));
const tHide = computed(() => props.labelUnlock ?? t('buttons.hide'));

const iconOk = computed(() => props.okIcon ?? 'pi pi-check');
const iconNotOk = computed(() => props.notOkIcon ?? 'pi pi-times');
const colorOk = computed(() => props.okIconColor ?? 'green');
const colorNotOk = computed(() => props.notOkIconColor ?? 'red');
const statusSize = computed(() => props.statusIconSize ?? '3rem');
const iconForce = computed(() => props.forceIcon ?? 'pi pi-lock');
const iconUndoForce = computed(() => props.undoForceIcon ?? 'pi pi-lock-open');
const iconShow = computed(() => props.showIcon);
const iconHide = computed(() => props.hideIcon);

/** UI state */
const showTasks = ref<boolean>(true);

/** Sync rule (core):
 * ready := forced || naturalReady
 * showTasks := !ready
 */
function syncFromSources() {
  const eff = effectiveReady.value;
  if (!isHydrating.value && ready.value !== eff) {
    ready.value = eff;
  }
  showTasks.value = !eff;
}

/** Initial sync and reactive updates */
watch([naturalReady, forced], syncFromSources, { immediate: true });

/** Actions */
function forceContinue() {
  forced.value = true; // triggers sync
  emit('force-continue');
}
function revertContinue() {
  forced.value = false; // triggers sync
  emit('revert-continue');
}
function toggle() {
  // toggling ready directly isn't meaningful anymore; we keep it for legacy callers:
  // If user toggles on: prefer forcing when natural is false. If toggles off: remove force.
  if (!ready.value) {
    if (!naturalReady.value) forced.value = true;
  } else {
    forced.value = false;
  }
}
</script>

<style scoped>
.ui-plugin-wrapper {
  display: block;
}
</style>
